import { apolloClient } from '../api/client.js';
import { accountsClient, accountsPassword, normalizePhone } from './accounts.js';
import { useAuthStore } from '../stores/authStore.js';
import { GET_USER, EMAIL_BY_PHONE, TOKEN_BY_PHONE } from '../../graphql/queries/index.js';
import { STORAGE_KEYS } from '../../config/env.js';

/**
 * Restore session on app mount
 */
export async function restoreSession() {
  const { setUser, setTokens, setLoading, clear } = useAuthStore.getState();
  setLoading(true);

  try {
    const tokens = await accountsClient.refreshSession();
    if (tokens?.accessToken) {
      setTokens(tokens.accessToken);
    }

    const userId = localStorage.getItem(STORAGE_KEYS.userId);
    if (userId) {
      const { data } = await apolloClient.query({
        query: GET_USER,
        variables: { userId },
        fetchPolicy: 'network-only',
      });
      if (data?.getUser) {
        setUser(data.getUser);
        setLoading(false);
        return data.getUser;
      }
    }
  } catch {
    clear();
  }

  setLoading(false);
  return null;
}

/**
 * Request SMS code via legacy-compatible flow
 * @param {string} phone
 */
export async function requestSmsCode(phone) {
  const normalized = normalizePhone(phone);
  const { data } = await apolloClient.query({
    query: EMAIL_BY_PHONE,
    variables: { phoneNumber: normalized },
    fetchPolicy: 'network-only',
  });

  const email = data?.emailByPhone;
  if (!email) {
    throw new Error('User not found');
  }

  await accountsPassword.requestPasswordReset(email);
  return normalized;
}

/**
 * Verify SMS code and establish session
 * @param {string} phone
 * @param {string} code
 */
export async function verifySmsCode(phone, code) {
  const normalized = normalizePhone(phone);
  const trimmedCode = String(code ?? '').trim();
  const { data } = await apolloClient.query({
    query: TOKEN_BY_PHONE,
    variables: { phoneNumber: normalized, code: trimmedCode || undefined },
    fetchPolicy: 'network-only',
  });

  const tokenData = data?.tokenByPhone;
  if (!tokenData || String(tokenData.loginCode) !== trimmedCode) {
    throw new Error('Invalid code');
  }

  if (!tokenData.loginToken) {
    throw new Error('Login session expired. Please request a new code.');
  }

  const loginResult = await accountsPassword.resetPassword(tokenData.loginToken, trimmedCode);
  if (!loginResult?.tokens?.accessToken) {
    throw new Error('Unable to establish session');
  }

  await accountsClient.setTokens(loginResult.tokens);
  useAuthStore.getState().setTokens(loginResult.tokens.accessToken);
  localStorage.setItem(STORAGE_KEYS.userId, tokenData.userId);

  const { data: userData } = await apolloClient.query({
    query: GET_USER,
    variables: { userId: tokenData.userId },
    fetchPolicy: 'network-only',
  });

  const user = userData?.getUser;
  if (user) {
    useAuthStore.getState().setUser(user);
  }

  return user;
}

/**
 * Logout and clear all session data
 */
export async function logout() {
  try {
    await accountsClient.logout();
  } catch {
    // ignore logout errors
  }

  await apolloClient.clearStore();
  localStorage.removeItem(STORAGE_KEYS.userId);
  localStorage.removeItem(STORAGE_KEYS.originalUser);
  useAuthStore.getState().clear();
}

/**
 * @returns {boolean}
 */
export function isAuthenticated() {
  return !!useAuthStore.getState().currentUser;
}
